import axios, { AxiosError, AxiosInstance, AxiosRequestConfig } from 'axios';
import { getTenantId } from './tenantHeader.ts';

const shouldAttachTenantHeader = (url?: string) => {
  if (!url) return false;
  return !url.startsWith('/api/v1/super/')
    && !url.startsWith('/api/v1/auth/')
    && !url.startsWith('/api/v1/tenants');
};

// Simple UUID v4 generator
function uuid4(): string {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, c => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

const client: AxiosInstance = axios.create({ baseURL: '' });

// Request-ID + retry(1) on 502
client.interceptors.request.use((cfg) => {
  const requestId = uuid4();
  cfg.headers = cfg.headers || {};
  (cfg.headers as any)['X-Request-Id'] = requestId;
  (cfg as any).__retry = (cfg as any).__retry ?? 0;
  const tid = getTenantId();
  if (tid && shouldAttachTenantHeader(cfg.url) && !(cfg.headers as any)['X-Tenant-Id']) {
    (cfg.headers as any)['X-Tenant-Id'] = String(tid);
  }
  const token = localStorage.getItem('accessToken');
  if (token) {
    (cfg.headers as any).Authorization = `Bearer ${token}`;
  }
  return cfg;
});

client.interceptors.response.use(
  (res) => {
    const reqId = res.headers['x-request-id'] || (res.config.headers as any)['X-Request-Id'];
    if (reqId) console.debug('[REQ-ID]', reqId, res.config.method?.toUpperCase(), res.config.url, res.status);
    return res;
  },
  async (error: AxiosError) => {
    const res = error.response;
    const cfg: any = error.config || {};
    const reqId = res?.headers?.['x-request-id'] || (cfg.headers && cfg.headers['X-Request-Id']);
    if (reqId) console.error('[REQ-ID]', reqId, cfg.method?.toUpperCase(), cfg.url, res?.status);
    if (res && res.status === 502 && cfg && cfg.__retry < 1) {
      cfg.__retry = (cfg.__retry || 0) + 1;
      return client(cfg);
    }
    return Promise.reject(error);
  }
);

export const http = {
  get: (url: string, config?: AxiosRequestConfig) => client.get(url, config).then(r => r.data),
  post: (url: string, data?: any, config?: AxiosRequestConfig) => client.post(url, data, config).then(r => r.data),
  put: (url: string, data?: any, config?: AxiosRequestConfig) => client.put(url, data, config).then(r => r.data),
  del: (url: string, config?: AxiosRequestConfig) => client.delete(url, config).then(r => r.data),
  upload: (url: string, formData: FormData) => client.post(url, formData, { headers: { 'Content-Type': 'multipart/form-data' } }).then(r => r.data),
};

export default http;


